import { z } from "zod";
import {
  FEEDBACK_SEVERITIES,
  FEEDBACK_STATUS_FILTERS,
} from "@/features/feedback/feedback-vocabulary";

/// URL search-params schema for `/$slug/feedback`. The `scope`
/// param is the single tab dimension on the unified feedback page: it
/// replaces the old separate `/admin/feedback` route and rolls the old
/// `view` + `role` controls into one progressive-disclosure axis.
///
///   for-me    , feedback I received (everyone)
///   from-me   , feedback I authored (everyone)
///   all       , everything I can see (chain visibility) (everyone)
///   workspace , every feedback in the workspace, bypasses chain
///                visibility (admins via FEEDBACK_VIEW_WORKSPACE)
///
/// Tab visibility is enforced in the toolbar (hide tabs the caller can't
/// use) AND in the route (refuse-by-redirect when a scope is hit without
/// the role). Defence in depth, bookmarked URLs can't bypass the gate.
///
/// "Team view" + "Inbox" were dropped, both were redundant lenses on
/// `all` (one subtracted my involvement, the other regrouped by
/// counterpart) rather than different perspectives. Managers reach
/// team views via the assignee/recipient filter; counterpart grouping
/// can be a future polish on `all`.
export const FEEDBACK_SCOPES = ["team", "for-me", "from-me", "all", "workspace"] as const;
export type FeedbackScope = (typeof FEEDBACK_SCOPES)[number];

export const feedbackRouteSearchSchema = z
  .object({
    scope: z.enum(FEEDBACK_SCOPES).catch("team").default("team"),
    severity: z.enum(FEEDBACK_SEVERITIES).optional(),
    status: z.enum(FEEDBACK_STATUS_FILTERS).optional(),
    since: z.string().optional(),
    withActionPlan: z.boolean().optional(),
    recipientUserId: z.string().optional(),
    authorUserId: z.string().optional(),
    /// Relationship-board deep link: restrict to feedback exchanged with this
    /// counterpart (server-side "between us" filter). `counterpartName` is
    /// carried only to label the removable filter chip without a lookup.
    counterpartId: z.string().optional(),
    counterpartName: z.string().optional(),
  })
  .strict();
export type FeedbackRouteSearch = z.infer<typeof feedbackRouteSearchSchema>;
